import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Trans, useTranslation } from "react-i18next";
import { ArrowLeft, Search, AlertCircle, Loader2, User } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useAuth } from "../../context/AuthContext";
import { EntityCard } from "./EntityCard";
import { JoinConfirmModal } from "./JoinConfirmModal";
import { AuthIllustration } from "./AuthIllustration";
import { onboardErrorMessage } from "./onboardErrors";
import type {
  CompanyEntry,
  EntitiesResponse,
  LookupEntitiesResponse,
  SmartIdUserInfo,
} from "../../types/smartId";

const API_URL = process.env.REACT_APP_API_URL || "";

export function SelectRolePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refreshAuth, isAuthenticated } = useAuth();

  const [userInfo, setUserInfo] = useState<SmartIdUserInfo | null>(null);
  const [companies, setCompanies] = useState<CompanyEntry[]>([]);
  // Whitelisted at login → instant onboarding; otherwise register/join files an access request (ticket 6).
  const [trusted, setTrusted] = useState(false);
  const [personalCodeInput, setPersonalCodeInput] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState("");
  const [selfOnboardingAvailable, setSelfOnboardingAvailable] = useState(true);
  const [joinTarget, setJoinTarget] = useState<{ entry: CompanyEntry; role: "building" | "provider" } | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
      return;
    }
    fetchEntities();
  }, [isAuthenticated]);

  const fetchEntities = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get<{ success: boolean; data: EntitiesResponse }>(
        `${API_URL}/auth/smartid/entities`,
        { withCredentials: true }
      );
      if (res.data.success) {
        setUserInfo(res.data.data.userInfo);
        setCompanies(res.data.data.companies);
        setTrusted(res.data.data.trusted === true);
        // Absent on an older backend — assume available so the CTAs are not
        // disabled for the wrong reason.
        setSelfOnboardingAvailable(res.data.data.selfOnboardingAvailable !== false);
        if (res.data.data.userInfo?.personalCode) {
          setPersonalCodeInput(res.data.data.userInfo.personalCode);
        }
      }
    } catch (err) {
      // No Smart-ID session: nothing on this page can work without one, and the
      // empty frame it used to render said nothing at all — no identity, no
      // organisations, no way on (BUG-6). Send the visitor where they can log in.
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      // Anything else is the server failing, not the visitor being logged out;
      // keep them here and say so rather than bouncing them to a login they
      // have already done.
      setError(t("selectRole.errors.loadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLookup = async () => {
    const code = personalCodeInput.trim();
    if (!code) return;
    setIsWorking(true);
    setError("");
    try {
      const res = await axios.post<{ success: boolean; data: LookupEntitiesResponse }>(
        `${API_URL}/auth/smartid/lookup-entities`,
        { personalCode: code },
        { withCredentials: true }
      );
      if (res.data.success) {
        // Same status-enriched shape as /entities — badges survive a manual refresh.
        setCompanies(res.data.data.companies);
        setTrusted(res.data.data.trusted === true);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t("selectRole.errors.lookupFailed"));
    } finally {
      setIsWorking(false);
    }
  };

  const selectEntity = async (entityType: "building" | "service_provider", entityId: string) => {
    setIsWorking(true);
    setError("");
    try {
      const res = await axios.post(
        `${API_URL}/auth/smartid/select`,
        { entityType, entityId },
        { withCredentials: true }
      );
      if (res.data.success) {
        await refreshAuth();
        navigate("/dashboard", { replace: true });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t("selectRole.errors.selectFailed"));
    } finally {
      setIsWorking(false);
    }
  };

  const joinProvider = async (registryCode: string) => {
    setIsWorking(true);
    setError("");
    try {
      const res = await axios.post(
        `${API_URL}/api/service-providers/onboard`,
        { registryCode },
        { withCredentials: true }
      );
      if (res.data.success) {
        await refreshAuth();
        navigate("/dashboard", { replace: true });
      }
    } catch (err: any) {
      setError(onboardErrorMessage(err, t));
    } finally {
      setIsWorking(false);
      setJoinTarget(null);
    }
  };

  const joinBuilding = async (registryCode: string) => {
    setIsWorking(true);
    setError("");
    try {
      const res = await axios.post(
        `${API_URL}/api/buildings/onboard`,
        { registryCode },
        { withCredentials: true }
      );
      if (res.data.success) {
        await refreshAuth();
        navigate("/dashboard", { replace: true });
      }
    } catch (err: any) {
      setError(onboardErrorMessage(err, t));
    } finally {
      setIsWorking(false);
      setJoinTarget(null);
    }
  };

  const userName = userInfo?.givenName
    ? `${userInfo.givenName} ${userInfo.familyName || ""}`.trim()
    : null;

  // Ticket 19: a `membership` row may carry no registry code, so the card's
  // identity is the entity it names — the code is only a fallback for a
  // registry hint, which by definition always has one.
  const entityKey = (entry: CompanyEntry): string =>
    entry.building?.id ?? entry.provider?.id ?? `code:${entry.registryCode}`;

  const yours = companies.filter((c) => c.buildingLinked || c.providerLinked);
  const found = companies.filter((c) => !c.buildingLinked && !c.providerLinked);

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 bg-card px-4 sm:px-6 py-5 flex flex-col overflow-x-hidden">
        <Link to="/" className="w-fit group flex items-center gap-2">
          <img src="/logo.png" alt="Unikort" className="h-9 w-auto" />
          <span className="text-xl font-semibold text-[#5756d6] dark:text-[#777aff]">Unikort</span>
        </Link>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-[560px]">
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                {t("selectRole.yourOrgs")}
              </h1>
              {userName && (
                <div className="flex items-center gap-2 mt-3 text-muted-foreground">
                  <User className="w-4 h-4" />
                  <span className="text-sm">
                    <Trans
                      i18nKey="selectRole.greeting"
                      values={{ name: userName }}
                      components={{ 1: <span className="font-medium text-foreground" /> }}
                    />
                  </span>
                </div>
              )}
            </div>

            {/* The code is fixed to the authenticated identity — the server only
                looks up your own isikukood, so this is a read-only display with a
                refresh action, not a search box for arbitrary codes. */}
            <div className="mb-6">
              <Label htmlFor="personal-code" className="text-sm font-medium mb-2 block">
                {t("selectRole.personalCode")}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="personal-code"
                  type="text"
                  value={personalCodeInput}
                  readOnly
                  disabled={isWorking || isLoading}
                  className="font-mono"
                />
                <Button
                  onClick={handleLookup}
                  disabled={isWorking || isLoading || !personalCodeInput.trim()}
                  title={t("selectRole.refresh")}
                >
                  {isWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-4 rounded-xl border border-destructive/20 mb-4 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5" />
                {error}
              </div>
            )}

            {/* Said once, up front: registering or joining cannot succeed
                until an operator provisions membership (ticket 13 §C/§H). */}
            {!isLoading && !selfOnboardingAvailable && (
              <div className="bg-muted text-muted-foreground text-sm p-4 rounded-xl border border-border mb-4 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {t("selectRole.selfOnboardingUnavailable")}
              </div>
            )}

            {isLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-6">
                {yours.length > 0 && (
                  <section>
                    <div className="text-xs font-medium text-primary uppercase tracking-wide mb-3">
                      {t("selectRole.yourOrgs")}
                    </div>
                    <div className="space-y-2">
                      {yours.map((entry) => (
                        <EntityCard
                          key={entityKey(entry)}
                          entry={entry}
                          disabled={isWorking}
                          onLoginBuilding={(id) => selectEntity("building", id)}
                          onLoginProvider={(id) => selectEntity("service_provider", id)}
                          onRegisterBuilding={(code) => navigate(`/onboarding?registryCode=${code}`)}
                          onRegisterProvider={(code) => navigate(`/onboarding-provider?registryCode=${code}`)}
                          onJoinBuilding={(e) => setJoinTarget({ entry: e, role: "building" })}
                          onJoinProvider={(e) => setJoinTarget({ entry: e, role: "provider" })}
                          trusted={trusted}
                          onRequestAccess={(e, role) =>
                            e.registryCode &&
                            navigate(`/access-request?registryCode=${e.registryCode}&role=${role}`)
                          }
                          selfOnboardingUnavailable={!selfOnboardingAvailable}
                        />
                      ))}
                    </div>
                  </section>
                )}
                {found.length > 0 && (
                  <section>
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                      {t("selectRole.foundInRegistry")}
                    </div>
                    <div className="space-y-2">
                      {found.map((entry) => (
                        <EntityCard
                          key={entityKey(entry)}
                          entry={entry}
                          disabled={isWorking}
                          onLoginBuilding={(id) => selectEntity("building", id)}
                          onLoginProvider={(id) => selectEntity("service_provider", id)}
                          onRegisterBuilding={(code) => navigate(`/onboarding?registryCode=${code}`)}
                          onRegisterProvider={(code) => navigate(`/onboarding-provider?registryCode=${code}`)}
                          onJoinBuilding={(e) => setJoinTarget({ entry: e, role: "building" })}
                          onJoinProvider={(e) => setJoinTarget({ entry: e, role: "provider" })}
                          trusted={trusted}
                          onRequestAccess={(e, role) =>
                            e.registryCode &&
                            navigate(`/access-request?registryCode=${e.registryCode}&role=${role}`)
                          }
                          selfOnboardingUnavailable={!selfOnboardingAvailable}
                        />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-border">
              <Button variant="ghost" className="w-full" onClick={() => navigate("/login")} disabled={isWorking}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("selectRole.back")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary to-primary-hover items-center justify-center">
        <AuthIllustration />
      </div>

      <JoinConfirmModal
        open={joinTarget !== null}
        name={joinTarget?.entry.name ?? ""}
        role={joinTarget?.role ?? "provider"}
        isSubmitting={isWorking}
        onCancel={() => setJoinTarget(null)}
        onConfirm={() => {
          if (!joinTarget) return;
          // Joining is an onboarding action keyed on the registry code, so it
          // exists only for an entry that has one — a membership row (which may
          // not) can never open this modal: it is already linked (ticket 19).
          const code = joinTarget.entry.registryCode;
          if (!code) return;
          if (joinTarget.role === "provider") joinProvider(code);
          else joinBuilding(code);
        }}
      />
    </div>
  );
}
